import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ChatFireService } from '../../../gen2ai/chatfire.service';
import { Conversation } from '../../../models/chat.model';
import {
  BETA_MENU_SECTIONS,
  BetaMenuLink,
  BetaMenuSection,
  betaMenuPathFromRouterUrl,
  betaSectionMatchesRoute,
  findActiveBetaSectionId
} from './beta-menu-sections.config';

@Component({
  selector: 'app-beta-menu',
  templateUrl: './beta-menu.component.html',
  styleUrls: ['./beta-menu.component.scss']
})
export class BetaMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('rail', { static: false }) railRef?: ElementRef<HTMLElement>;
  @ViewChild('panel', { static: false }) panelRef?: ElementRef<HTMLElement>;

  private readonly compactBreakpoint = 1024;
  isMenuOpen = false;
  isPinnedOpen = false;
  activeSectionId = 'chat';
  isLoggedIn = false;
  isCompactMenu = true;
  private panelRafId: number | null = null;
  private readonly destroy$ = new Subject<void>();
  readonly authState$: Observable<boolean>;

  recentConversations: Conversation[] = [];
  loadingConversations = false;
  hoveredConversationId: string | null = null;
  editingTopicId: string | null = null;
  editingTopicValue = '';

  textSizeScale = 1.15;
  iconSizeScale = 1.2;
  private readonly minScale = 1;
  private readonly maxScale = 1.3;
  private readonly scaleStep = 0.1;

  readonly sections: readonly BetaMenuSection[] = BETA_MENU_SECTIONS;

  constructor(
    private router: Router,
    private chatService: ChatFireService
  ) {
    this.authState$ = this.chatService.authState$;
  }

  ngOnInit(): void {
    this.updateViewportMode();

    this.chatService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        if (isLoggedIn) {
          void this.loadRecentConversations();
        } else {
          this.recentConversations = [];
        }
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const path = betaMenuPathFromRouterUrl(this.router.url);
        const activeId = findActiveBetaSectionId(path, this.sections);
        if (activeId) {
          this.activeSectionId = activeId;
        }
        if (this.isLoggedIn) {
          setTimeout(() => void this.loadRecentConversations(), 500);
        }
      });

    const path = betaMenuPathFromRouterUrl(this.router.url);
    const activeId = findActiveBetaSectionId(path, this.sections);
    if (activeId) {
      this.activeSectionId = activeId;
    }
  }

  ngAfterViewInit(): void {
    this.schedulePanelPosition();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.panelRafId !== null) {
      cancelAnimationFrame(this.panelRafId);
      this.panelRafId = null;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewportMode();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isPinnedOpen || !this.isMenuOpen) return;

    const target = event.target as Node;
    const clickedRail = this.railRef?.nativeElement.contains(target) ?? false;
    const clickedPanel = this.panelRef?.nativeElement.contains(target) ?? false;

    if (!clickedRail && !clickedPanel) {
      this.isMenuOpen = false;
    }
  }

  get activeSection(): BetaMenuSection {
    return this.sections.find((section) => section.id === this.activeSectionId) ?? this.sections[0];
  }

  onIconClick(sectionId: string): void {
    this.activeSectionId = sectionId;
    this.isMenuOpen = true;
    this.schedulePanelPosition();
  }

  setActiveSection(sectionId: string): void {
    this.activeSectionId = sectionId;
    if (!this.isCompactMenu) {
      this.isMenuOpen = true;
      this.schedulePanelPosition();
    }
  }

  togglePinnedOpen(event: Event): void {
    event.preventDefault();
    this.isPinnedOpen = !this.isPinnedOpen;
    this.isMenuOpen = this.isPinnedOpen || this.isMenuOpen;
  }

  trackBySection(index: number, section: BetaMenuSection): string {
    return section.id;
  }

  trackByLink(index: number, link: BetaMenuLink): string {
    return `${link.route}-${index}`;
  }

  trackByConversationId(index: number, conv: Conversation): string {
    return conv.id;
  }

  async loadRecentConversations(): Promise<void> {
    if (!this.isLoggedIn) {
      this.recentConversations = [];
      return;
    }
    try {
      this.loadingConversations = true;
      const conversations = await firstValueFrom(this.chatService.getRecentConversations(6));
      this.recentConversations = this.filterDisplayableConversations(conversations);
    } catch (err) {
      console.error('[BetaMenu] Error loading conversations', err);
      this.recentConversations = [];
    } finally {
      this.loadingConversations = false;
    }
  }

  async loadConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        this.router.navigate(['/ai/clarichat'], { queryParams: { conversationId } });
      }
    } catch (error) {
      console.error('[BetaMenu] Error loading conversation', error);
    }
  }

  truncateTopic(topic: string): string {
    if (!topic) return 'Classified Conversation';
    return topic.length <= 17 ? topic : `${topic.substring(0, 17)}...`;
  }

  startEditingTopic(conversationId: string, currentTopic: string): void {
    this.editingTopicId = conversationId;
    this.editingTopicValue = currentTopic || '';
    requestAnimationFrame(() => {
      const root = this.panelRef?.nativeElement;
      const input = root?.querySelector<HTMLInputElement>('.beta-conversation-topic-input');
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  async saveTopic(conversationId: string): Promise<void> {
    if (!this.editingTopicId || !this.editingTopicValue.trim()) {
      this.cancelEditingTopic();
      return;
    }
    try {
      const conversation = this.recentConversations.find((c) => c.id === conversationId);
      if (conversation) {
        const topic = this.editingTopicValue.trim();
        conversation.topic = topic;
        await firstValueFrom(this.chatService.updateConversation({ ...conversation, topic }));
        await this.loadRecentConversations();
      }
    } catch (err) {
      console.error('[BetaMenu] Error saving topic', err);
    } finally {
      this.cancelEditingTopic();
    }
  }

  cancelEditingTopic(): void {
    this.editingTopicId = null;
    this.editingTopicValue = '';
  }

  getSectionIconClass(): string {
    const iconMap: Record<string, string> = {
      chat: 'fa-solid fa-seedling',
      clarigen: 'fa-solid fa-brain',
      instruments: 'fa-solid fa-guitar'
    };
    return iconMap[this.activeSectionId] ?? 'fa-solid fa-comment-dots';
  }

  increaseTextSize(): void { this.textSizeScale = this.getBoundedScale(this.textSizeScale + this.scaleStep); }
  decreaseTextSize(): void { this.textSizeScale = this.getBoundedScale(this.textSizeScale - this.scaleStep); }
  increaseIconSize(): void { this.iconSizeScale = this.getBoundedScale(this.iconSizeScale + this.scaleStep); }
  decreaseIconSize(): void { this.iconSizeScale = this.getBoundedScale(this.iconSizeScale - this.scaleStep); }

  private updatePanelPosition(): void {
    if (this.railRef && this.panelRef) {
      const railRect = this.railRef.nativeElement.getBoundingClientRect();
      const panel = this.panelRef.nativeElement;
      panel.style.left = `${railRect.right + 12}px`;
      panel.style.top = `${railRect.top + 12}px`;
    }
  }

  private isRouteInSection(sectionId: string): boolean {
    const section = this.sections.find((item) => item.id === sectionId);
    if (!section) return false;
    return betaSectionMatchesRoute(section, betaMenuPathFromRouterUrl(this.router.url));
  }

  private updateViewportMode(): void {
    this.isCompactMenu = this.getViewportWidth() <= this.compactBreakpoint;
    if (this.isCompactMenu && !this.isPinnedOpen) {
      this.isMenuOpen = false;
    }
  }

  private getViewportWidth(): number {
    return typeof window === 'undefined' ? 0 : window.innerWidth;
  }

  private schedulePanelPosition(): void {
    if (this.panelRafId !== null) cancelAnimationFrame(this.panelRafId);
    this.panelRafId = requestAnimationFrame(() => {
      this.panelRafId = null;
      this.updatePanelPosition();
    });
  }

  private filterDisplayableConversations(conversations: Conversation[]): Conversation[] {
    return conversations.filter((conversation) => {
      const messages = conversation.messages ?? [];
      if (messages.length < 2) return false;
      return messages.some((m) => m.role === 'user') && messages.some((m) => m.role === 'assistant');
    });
  }

  private getBoundedScale(scale: number): number {
    return Number(Math.min(this.maxScale, Math.max(this.minScale, scale)).toFixed(2));
  }

  onAuthLinkClick(event: Event, link?: BetaMenuLink): void {
    const isAuthLink = link?.label === 'Log In' || link?.route === '/auth/log-in';
    if (!isAuthLink || !this.isLoggedIn) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.chatService.logout()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.afterAuthLogout(),
        error: () => this.afterAuthLogout()
      });
  }

  private afterAuthLogout(): void {
    this.isLoggedIn = false;
    this.recentConversations = [];
    void this.router.navigate(['/auth/log-in'], { queryParams: { loggedOut: 'true' } });
  }
}